import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import logoSecondLane from '@/assets/logoSecondLane.svg'
import { cn } from '@/utils/cn'
import type { AutosaveState } from '@/hooks/useDebouncedAutosave'
import { BODY_TEXT, CARD_SHELL, HELP_TEXT, PAGE_TITLE } from './styles'

export interface WizardStepDef<Id extends string> {
  id: Id
  label: string
}

interface WizardShellProps<Id extends string> {
  steps: readonly WizardStepDef<Id>[]
  currentStep: Id
  onStepSelect?: (step: Id) => void
  title: string
  subtitle?: string
  saveState: AutosaveState
  saveError: string | null
  lastSavedAt: Date | null
  children: ReactNode
  footer?: ReactNode
}

const SaveIndicator = ({
  state,
  error,
  lastSavedAt,
}: {
  state: AutosaveState
  error: string | null
  lastSavedAt: Date | null
}) => {
  if (state === 'error') {
    return (
      <span className="font-plus-jakarta text-[12px] leading-[18px] text-[#ff540c]">
        {error || 'Could not save your progress'} — your answers are still here; we will retry as
        you keep typing.
      </span>
    )
  }
  if (state === 'saving') {
    return <span className={HELP_TEXT}>Saving…</span>
  }
  if (state === 'pending') {
    return <span className={HELP_TEXT}>Unsaved changes</span>
  }
  if (state === 'saved' && lastSavedAt) {
    return (
      <span className={cn(HELP_TEXT, 'inline-flex items-center gap-1')}>
        <Check size={12} className="text-[#1fb845]" />
        Progress saved at{' '}
        {lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    )
  }
  return <span className={HELP_TEXT}>Your progress is saved automatically.</span>
}

/**
 * Chrome for the institutional signup wizard: logo, stepper, autosave status,
 * and the card the current step renders into.
 *
 * Single implementation for both breakpoints — the stepper collapses to a
 * horizontal scroll strip on narrow screens rather than becoming different
 * markup, so there is nothing for a Desktop/Mobile split to hold.
 */
export const WizardShell = <Id extends string>({
  steps,
  currentStep,
  onStepSelect,
  title,
  subtitle,
  saveState,
  saveError,
  lastSavedAt,
  children,
  footer,
}: WizardShellProps<Id>) => {
  const currentIndex = steps.findIndex(step => step.id === currentStep)

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:px-8">
      <div className="mx-auto w-full max-w-[860px]">
        <img
          src={logoSecondLane}
          alt="SecondLane"
          className="mb-8 h-6 w-24 dark:brightness-0 dark:invert"
        />

        <h1 className={PAGE_TITLE}>{title}</h1>
        {subtitle && <p className={cn(BODY_TEXT, 'mt-2 max-w-[640px]')}>{subtitle}</p>}

        <ol className="mt-6 mb-4 flex gap-2 overflow-x-auto pb-1">
          {steps.map((step, index) => {
            const isCurrent = step.id === currentStep
            const isDone = index < currentIndex
            // Only steps already visited are clickable: jumping ahead would skip
            // the answers a later step depends on.
            const isReachable = index <= currentIndex && !!onStepSelect
            return (
              <li key={step.id} className="shrink-0">
                <button
                  type="button"
                  disabled={!isReachable}
                  onClick={() => onStepSelect?.(step.id)}
                  aria-current={isCurrent ? 'step' : undefined}
                  className={cn(
                    'flex items-center gap-2 border px-3 py-2 font-plus-jakarta text-[13px] leading-[18px] transition-colors',
                    isCurrent
                      ? 'border-[#2d2923] bg-[#2d2923] text-white dark:border-[#fdfdfd] dark:bg-[#fdfdfd] dark:text-[#2d2923]'
                      : 'border-[rgba(45,41,35,0.16)] text-[rgba(45,41,35,0.64)] dark:border-[rgba(253,253,253,0.16)] dark:text-[rgba(253,253,253,0.64)]',
                    isReachable && !isCurrent && 'hover:border-[rgba(45,41,35,0.4)] cursor-pointer',
                    !isReachable && 'cursor-default',
                  )}
                >
                  {/* Fixed-width marker slot: the tick and the "1." glyphs are
                      different widths, so without it every completed step
                      re-flows the whole strip sideways on each Continue. */}
                  <span className="inline-flex min-w-[14px] shrink-0 items-center justify-center">
                    {isDone ? (
                      <Check size={14} className="text-[#1fb845]" />
                    ) : (
                      <span aria-hidden>{index + 1}.</span>
                    )}
                  </span>
                  {step.label}
                </button>
              </li>
            )
          })}
        </ol>

        <div className="mb-3 min-h-[18px]">
          <SaveIndicator state={saveState} error={saveError} lastSavedAt={lastSavedAt} />
        </div>

        <div className={cn(CARD_SHELL, 'px-4 py-6 sm:px-8 sm:py-8')}>{children}</div>

        {footer && <div className="mt-6">{footer}</div>}
      </div>
    </div>
  )
}
